import React, { useMemo } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { useConfig } from '../../context/ConfigContext';
import { optimizeEmployeeWithEligibility } from '../../utils/calculations';

/**
 * Section 125 Summary — mirrors the Workforce/admin BulkView totals layout
 * for this client. Numbers are recomputed live from the census so they always
 * match the Workforce tab regardless of cached `client.summary` values.
 *
 * Layout (compact for sidebar use):
 *   - Top row: 6 KPI tiles (Total Employees / Eligible / Waiting / Ineligible /
 *     Not Recommended / Total Employee Benefit/yr)
 *   - Middle row: 2 large tiles (Total Employer FICA/yr, HC Spread Revenue/yr)
 *   - Bottom: Eligibility Breakdown (1099 / hours / owner / union / waiting / salary)
 *     — only renders categories with non-zero counts.
 */
export default function Section125SummarySection({ client }) {
  const { config } = useConfig();
  const employees = client?.employees || [];
  const hasCensus = employees.length > 0;

  const totals = useMemo(() => deriveSection125Totals(employees, config), [employees, config]);

  if (!hasCensus || !totals) {
    return (
      <div data-testid="section125-summary-section" style={{ ...S.card, padding: 18, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <SectionHeader title="Section 125 Summary" icon="trending-up-outline" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ padding: '32px 12px', textAlign: 'center', background: C.bg, borderRadius: 10, width: '100%' }}>
            <Icon name="cloud-upload-outline" style={{ fontSize: 26, color: C.textLight, marginBottom: 8 }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>Upload your census</div>
            <div style={{ fontSize: 11, color: C.textMid }}>Your Section 125 summary will appear here once a census is uploaded.</div>
          </div>
        </div>
      </div>
    );
  }

  const tiles = [
    { label: 'Total Employees',           value: totals.count,            bg: '#fff',              color: C.text,      border: true },
    { label: 'Eligible',                  value: totals.eligible,         bg: C.greenLight || '#ecfdf5', color: C.green, sub: totals.count > 0 ? `${pct(totals.eligible / totals.count)} participation` : '' },
    { label: 'Waiting Period',            value: totals.waiting,          bg: totals.waiting > 0      ? (C.orangeLight || '#fffbeb') : '#fff', color: totals.waiting > 0 ? C.orange : C.textLight, border: totals.waiting === 0 },
    { label: 'Ineligible',                value: totals.ineligible,       bg: totals.ineligible > 0   ? (C.redLight    || '#fef2f2') : '#fff', color: totals.ineligible > 0 ? C.red : C.textLight, sub: totals.ineligible_salary > 0 ? `${totals.ineligible_salary} salary too low` : '', border: totals.ineligible === 0 },
    { label: 'Total Employee Benefit/Yr', value: fmtK(totals.empBenefit), bg: C.greenLight || '#ecfdf5', color: C.green },
  ];

  const breakdownItems = [
    { id: '1099',    label: '1099 Contractors', value: totals.ineligible_1099,  bg: C.redLight || '#fef2f2',    color: C.red },
    { id: 'hours',   label: 'Below Hours Min',  value: totals.ineligible_hours, bg: C.redLight || '#fef2f2',    color: C.red },
    { id: 'owner',   label: '>2% Shareholder',  value: totals.ineligible_owner, bg: C.redLight || '#fef2f2',    color: C.red },
    { id: 'union',   label: 'Union Excluded',   value: totals.ineligible_union, bg: C.redLight || '#fef2f2',    color: C.red },
    { id: 'salary',  label: 'Salary Too Low (min-wage floor)', value: totals.ineligible_salary, bg: C.redLight || '#fef2f2', color: C.red },
    { id: 'waiting', label: 'In Waiting Period',value: totals.waiting,           bg: C.orangeLight || '#fffbeb', color: C.orange },
  ].filter((b) => b.value > 0);

  return (
    <div data-testid="section125-summary-section" style={{ ...S.card, padding: 18, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SectionHeader title="Section 125 Summary" icon="trending-up-outline" />

      {/* Top KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {tiles.map((t, i) => (
          <div
            key={i}
            data-testid={`s125-tile-${slug(t.label)}`}
            style={{
              background: t.bg,
              border: t.border ? `1px solid ${C.border}` : 'none',
              borderRadius: 10,
              padding: '10px 8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 700 }}>{t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
            {t.sub && <div style={{ fontSize: 9, color: C.textLight, marginTop: 3 }}>{t.sub}</div>}
          </div>
        ))}
      </div>

      {/* Two big tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 8 }}>
        <div
          data-testid="s125-fica"
          style={{ background: C.blueLight || '#e8f2ff', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}
        >
          <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 700 }}>Total Employer FICA/Yr</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.blue, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{fmtK(totals.emprFica)}</div>
          <div style={{ fontSize: 9, color: C.textLight, marginTop: 3 }}>zero cost to employer</div>
        </div>
        <div
          data-testid="s125-hc-revenue"
          style={{ background: C.purpleLight || '#f5f3ff', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}
        >
          <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, fontWeight: 700 }}>HC Spread Revenue/Yr</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.purple, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{fmtK(totals.hcRevenue)}</div>
        </div>
      </div>

      {/* Eligibility Breakdown */}
      {breakdownItems.length > 0 && (
        <div
          data-testid="s125-eligibility-breakdown"
          style={{
            marginTop: 12, paddingTop: 12,
            borderTop: `1px solid ${C.borderLight}`,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textLight, marginBottom: 8 }}>
            Eligibility Breakdown
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {breakdownItems.map((b) => (
              <div
                key={b.id}
                data-testid={`s125-breakdown-${b.id}`}
                style={{ padding: '8px 10px', background: b.bg, borderRadius: 8 }}
              >
                <div style={{ fontSize: 10, color: C.textMid, marginBottom: 2 }}>{b.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: b.color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{b.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <Icon name={icon} style={{ fontSize: 18, color: C.accent }} />
      <h2 style={{ fontSize: 14, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}

function fmtK(n) {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v >= 1_000_000 ? 2 : 1).replace(/\.0$/, '') + 'k';
  return '$' + v.toLocaleString('en-US');
}
function pct(n) {
  if (!n && n !== 0) return '—';
  return Math.round(n * 1000) / 10 + '%';
}
function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Mirror BulkView's totals shape exactly so the Employer Portal numbers match
// what super users see on the Workforce tab.
export function deriveSection125Totals(employees, config) {
  if (!Array.isArray(employees) || employees.length === 0 || !config) return null;

  const analyzed = employees.map((e) => {
    const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
    if (!salaryNum) return e;
    try {
      return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
    } catch (err) {
      return e;
    }
  });

  const t = {
    count: analyzed.length,
    eligible: 0,
    waiting: 0,
    ineligible: 0,
    notRecommended: 0,
    empBenefit: 0,
    emprFica: 0,
    hcRevenue: 0,
    byPlan: {},
    ineligible_1099: 0,
    ineligible_hours: 0,
    ineligible_owner: 0,
    ineligible_union: 0,
    ineligible_salary: 0,
  };

  analyzed.forEach((e) => {
    if (e.eligibility) {
      if (e.eligibility.eligible && e.hasViable && e.optimal) {
        t.eligible++;
        t.empBenefit += e.optimal.employeeAnnualBenefit || 0;
        t.emprFica   += e.optimal.employerAnnualFica || 0;
        t.hcRevenue  += e.optimal.hcAnnualRevenue || 0;
        t.byPlan[e.optimal.label] = (t.byPlan[e.optimal.label] || 0) + 1;
      } else if (e.eligibility.status === 'waiting') {
        t.waiting++;
      } else if (e.eligibility.status === '1099')  { t.ineligible++; t.ineligible_1099++; }
      else if (e.eligibility.status === 'hours')   { t.ineligible++; t.ineligible_hours++; }
      else if (e.eligibility.status === 'owner')   { t.ineligible++; t.ineligible_owner++; }
      else if (e.eligibility.status === 'union')   { t.ineligible++; t.ineligible_union++; }
      else if (e.eligibility.status === 'salary')  { t.ineligible++; t.ineligible_salary++; }
    } else if (e.hasViable && e.optimal) {
      t.eligible++;
      t.empBenefit += e.optimal.employeeAnnualBenefit || 0;
      t.emprFica   += e.optimal.employerAnnualFica || 0;
      t.hcRevenue  += e.optimal.hcAnnualRevenue || 0;
      t.byPlan[e.optimal.label] = (t.byPlan[e.optimal.label] || 0) + 1;
    } else {
      // Legacy fallback (no eligibility object): treat as salary-too-low ineligible
      t.ineligible++;
      t.ineligible_salary++;
    }
  });

  return t;
}
